import React from 'react'
import { Modality, Valence } from '../../../api/client'
import CharCountField from '../../../components/inputs/CharCountField'
import { FIELD_CLASS, LABEL_CLASS } from '../../../components/inputs/fieldStyles'
import { MODALITY_OPTIONS } from '../../../models/Modality'
import { VALENCE_OPTIONS } from '../../../models/Valence'
import { NAME_MAX_LENGTH, SHORT_DESCRIPTION_MAX_LENGTH } from '../classifierFieldLimits'

/** The unversioned metadata both the create and edit forms collect. */
export type ClassifierMetadata = {
    displayName: string
    clientFacingName: string
    shortDescription: string
    modality: Modality
    valence: Valence
}

type Props = {
    value: ClassifierMetadata
    onChange: (value: ClassifierMetadata) => void
    /** Completes “… characters — shorten it to {overLimitAction}.” */
    overLimitAction: string
    required?: boolean
    /** Validation messages that belong under the name, e.g. an availability conflict. */
    nameIssues?: React.ReactNode
}

/** Whether anything in the form is over its column limit and so can't be saved. */
export function isMetadataOverLimit(value: ClassifierMetadata): boolean {
    return (
        value.displayName.length > NAME_MAX_LENGTH ||
        value.clientFacingName.length > NAME_MAX_LENGTH ||
        value.shortDescription.length > SHORT_DESCRIPTION_MAX_LENGTH
    )
}

/**
 * The metadata fields, shared by the create and edit forms so the two can't drift
 * apart on limits, labels or copy. Fully controlled — the surrounding form owns both
 * the value and what the submit button does with it.
 */
export default function ClassifierMetadataFields({
    value,
    onChange,
    overLimitAction,
    required,
    nameIssues
}: Props) {
    function set<K extends keyof ClassifierMetadata>(key: K, next: ClassifierMetadata[K]) {
        onChange({ ...value, [key]: next })
    }

    return (
        <>
            <CharCountField
                id="displayName"
                label="Name"
                value={value.displayName}
                onChange={(next) => set('displayName', next)}
                maxLength={NAME_MAX_LENGTH}
                required={required}
                overLimitMessage={`Names are limited to ${NAME_MAX_LENGTH} characters — shorten it to ${overLimitAction}.`}
            >
                {nameIssues}
            </CharCountField>
            <CharCountField
                id="clientFacingName"
                label="Client-facing name"
                value={value.clientFacingName}
                onChange={(next) => set('clientFacingName', next)}
                maxLength={NAME_MAX_LENGTH}
                required={required}
                overLimitMessage={`Names are limited to ${NAME_MAX_LENGTH} characters — shorten it to ${overLimitAction}.`}
            />
            <CharCountField
                id="shortDescription"
                label="Description"
                value={value.shortDescription}
                onChange={(next) => set('shortDescription', next)}
                maxLength={SHORT_DESCRIPTION_MAX_LENGTH}
                rows={2}
                required={required}
                overLimitMessage={`Descriptions are limited to ${SHORT_DESCRIPTION_MAX_LENGTH} characters — shorten it to ${overLimitAction}.`}
            />
            <div>
                <label htmlFor="modality" className={LABEL_CLASS}>
                    Modality
                </label>
                <select
                    id="modality"
                    value={value.modality}
                    onChange={(e) => set('modality', e.target.value as Modality)}
                    className={FIELD_CLASS}
                >
                    {MODALITY_OPTIONS.map(({ value: modality, label }) => (
                        <option key={modality} value={modality}>
                            {label}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <label htmlFor="valence" className={LABEL_CLASS}>
                    Valence
                </label>
                <select
                    id="valence"
                    value={value.valence}
                    onChange={(e) => set('valence', e.target.value as Valence)}
                    className={FIELD_CLASS}
                >
                    {VALENCE_OPTIONS.map(({ value: valence, label }) => (
                        <option key={valence} value={valence}>
                            {label}
                        </option>
                    ))}
                </select>
                <p className="mt-1 text-xs text-ink-500">
                    Only negative classifiers take part in moderation. Neutral and positive ones
                    are still scored and can still be filtered on.
                </p>
            </div>
        </>
    )
}
